// store/slices/authSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import AuthService from '../../services/authService';
import TokenService from '../../services/tokenService';
import { AUTH_STATUS } from '../../api/constants';

const storedUser = TokenService.getUser();
const storedToken = TokenService.getAccessToken();
const storedEmail = TokenService.hasActiveVerificationSession() ? TokenService.getStoredEmail() : null;
const isTokenValid = storedToken && !TokenService.isTokenExpired(storedToken);

// Derive accessDenied from the persisted validation result so page refresh
// doesn't silently re-grant access to a denied user.
const storedAccessDenied = isTokenValid && storedUser?.validationResult?.hasAccess === false;

const initialState = {
  email: storedEmail || null,
  token: isTokenValid ? storedToken : null,
  user: isTokenValid ? storedUser : null,
  status: 'idle',
  error: null,
  isVerified: isTokenValid && !!storedUser,
  awaitingVerification: !!storedEmail && !isTokenValid,
  timerValue: 0,
  accessDenied: storedAccessDenied,
  accessDeniedReason: storedAccessDenied ? (storedUser?.validationResult?.reason ?? 'unknown') : null,
  authMethod: null,
  validationResult: isTokenValid ? (storedUser?.validationResult ?? null) : null,
  isAuthReady: false
};

export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      // Check if we have valid tokens
      if (TokenService.hasValidTokens()) {
        const user = TokenService.getUser();
        const token = TokenService.getAccessToken();
        
        if (user && token) {
          return { 
            user, 
            token, 
            verified: true 
          };
        }
      }
      return rejectWithValue('No valid session found');
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email }, { rejectWithValue }) => {
    try {
      const result = await AuthService.login(email);
      // Store email in localStorage for persistence
      TokenService.setStoredEmail(email);
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const verifyCode = createAsyncThunk(
  'auth/verify',
  async ({ email, code }, { rejectWithValue }) => {
    try {
      const result = await AuthService.verifyCode(email, code);
      return result;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { dispatch, getState }) => {
    try {
      // AuthService logout - this handles the broadcast
      await AuthService.logout();
      
      // Clear all auth-related data from localStorage
      // Note: AuthService.logout already broadcasts, so we don't broadcast again
      TokenService.clearTokens({ broadcast: false });
      TokenService.setStoredEmail(null);
      
      // Clear all theme and cache data
      const THEME_CACHE_KEYS = [
        'kuppion_theme_cache_v3',
        'kuppion_theme_v2', 
        'kuppion_theme',
        'THEME_STORAGE_KEY'
      ];
      
      THEME_CACHE_KEYS.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.warn(`Failed to remove ${key}:`, error);
        }
      });
      
      // Reset theme state
      dispatch({ type: 'theme/resetTheme' });
      
      // Clear all RTK Query cache data
      const state = getState();
      
      // Clear individual API caches
      const apiReducerPaths = [
        'tenantApi',
        'campaignApi', 
        'announcementApi',
        'surveyApi',
        'userApi',
        'drawApi',
        'brandApi',
        'dashboardService'
      ];
      
      apiReducerPaths.forEach(path => {
        if (state[path]) {
          dispatch({
            type: `${path}/util/resetApiState`,
            payload: undefined
          });
        }
      });
      
      // Force clear any remaining cache
      if (typeof window !== 'undefined' && window.localStorage) {
        // Get all localStorage keys and remove any that might be cache-related
        const allKeys = Object.keys(localStorage);
        const cacheKeys = allKeys.filter(key => 
          key.includes('cache') || 
          key.includes('theme') || 
          key.includes('rtk') ||
          key.includes('tenant') ||
          key.includes('visual')
        );
        
        cacheKeys.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (error) {
            console.warn(`Failed to remove cache key ${key}:`, error);
          }
        });
      }
      
      return null;
    } catch (error) {
      console.error('Logout error:', error);
      // Force clear everything even if there's an error
      // Don't broadcast here to prevent loops
      TokenService.clearTokens({ broadcast: false });
      TokenService.setStoredEmail(null);
      
      // Try to clear theme cache anyway
      try {
        localStorage.removeItem('kuppion_theme_cache_v3');
        localStorage.removeItem('kuppion_theme_v2');
      } catch (e) {
        // Ignore errors
      }
      
      return null;
    }
  }
);

// Add check token validity thunk
export const checkTokenValidity = createAsyncThunk(
  'auth/checkTokenValidity',
  async (_, { dispatch, getState }) => {
    const { auth } = getState();
    
    if (auth.token) {
      const isValid = !TokenService.isTokenExpired(auth.token);
      if (!isValid) {
        // If token is invalid, logout
        dispatch(logout());
        return false;
      }
      return true;
    }
    return false;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthState: (state) => {
      state.email = null;
      state.token = null;
      state.user = null;
      state.status = 'idle';
      state.error = null;
      state.isVerified = false;
      state.awaitingVerification = false;
      state.timerValue = 0;
      state.accessDenied = false;
      state.accessDeniedReason = null;
      state.authMethod = null;
      state.validationResult = null;
      
      // Clear localStorage - don't broadcast to prevent loops
      TokenService.clearTokens({ broadcast: false });
      TokenService.setStoredEmail(null);
    },
    syncLogoutFromOtherTab: (state) => {
      state.email = null;
      state.token = null;
      state.user = null;
      state.status = 'idle';
      state.error = null;
      state.isVerified = false;
      state.awaitingVerification = false;
      state.timerValue = 0;
      state.accessDenied = false;
      state.accessDeniedReason = null;
      state.authMethod = null;
      state.validationResult = null;
      state.isAuthReady = true;
      // Note: TokenService.clearTokens is already called in useAuthSync with broadcast: false
    },
    clearLoginSession: (state) => {
      if (!state.isVerified) {
        state.email = null;
        state.awaitingVerification = false;
        state.timerValue = 0;
        state.error = null;
        
        TokenService.setStoredEmail(null);
        
        const VERIFICATION_KEYS = [
          'verification_end_time',
          'verification_max_time', 
          'verification_original_max_time',
          'last_code_request_time',
          'auto_resend_count'
        ];
        
        VERIFICATION_KEYS.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (error) {
            console.warn(`Failed to remove ${key}:`, error);
          }
        });
      }
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    updateTimerValue: (state, action) => {
      state.timerValue = action.payload;
    },
    clearUserCache: (state) => {
      console.log('Clearing user cache...');
    },
    setAuthReady: (state, action) => {
      state.isAuthReady = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isVerified = true;
        state.status = AUTH_STATUS.SUCCEEDED;
        state.error = null;
        state.isAuthReady = true;
        // Restore access-denied state from the persisted validation result.
        const denied = action.payload.user?.validationResult?.hasAccess === false;
        state.accessDenied = denied;
        state.accessDeniedReason = denied ? (action.payload.user?.validationResult?.reason ?? 'unknown') : null;
        state.validationResult = action.payload.user?.validationResult ?? null;
      })
      .addCase(restoreSession.rejected, (state) => {
        Object.assign(state, initialState);
        TokenService.clearTokens({ broadcast: false });
        TokenService.setStoredEmail(null);
        state.isAuthReady = true; // Kontrol bitti, oturum yok ama hazır
      })
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Email'i meta'dan al (dispatch sırasında gönderilen) veya payload'dan
        state.email = action.meta?.arg?.email || action.payload.email || TokenService.getStoredEmail();
        state.awaitingVerification = true;
        state.timerValue = action.payload.value || 180;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.awaitingVerification = false;
      })
      .addCase(verifyCode.pending, (state) => {
        state.status = 'loading';
        state.isAuthReady = true;
      })
      .addCase(verifyCode.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isVerified = true;
        state.awaitingVerification = false;
        state.error = null;
        state.isAuthReady = true;
        state.accessDenied = !!action.payload.accessDenied;
        state.accessDeniedReason = action.payload.accessDenied ? (action.payload.reason ?? 'unknown') : null;
        state.authMethod = action.payload.authMethod ?? 'unknown';
        state.validationResult = action.payload.user?.validationResult ?? null;
        TokenService.updateLastActivity();
      })
      .addCase(verifyCode.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.isAuthReady = true; // Giriş başarısız ama kontrol bitti, hazır
      })
      .addCase(logout.fulfilled, (state) => {
        state.email = null;
        state.token = null;
        state.user = null;
        state.status = AUTH_STATUS.IDLE;
        state.error = null;
        state.isVerified = false;
        state.awaitingVerification = false;
        state.timerValue = 0;
        state.accessDenied = false;
        state.accessDeniedReason = null;
        state.authMethod = null;
        state.validationResult = null;
        state.isAuthReady = true; // Çıkış yapıldı, yeni durumuyla hazır
      })
      .addCase(logout.rejected, (state) => {
        state.email = null;
        state.token = null;
        state.user = null;
        state.status = AUTH_STATUS.IDLE;
        state.error = null;
        state.isVerified = false;
        state.awaitingVerification = false;
        state.timerValue = 0;
        state.accessDenied = false;
        state.accessDeniedReason = null;
        state.authMethod = null;
        state.validationResult = null;
        state.isAuthReady = true; // Çıkış yapıldı, yeni durumuyla hazır
      });
  }
});

// **REMOVED: syncPermissionsMiddleware - No longer needed as permissions are managed in authSlice**

export const selectAuthState = (state) => state.auth;
export const selectEmail = (state) => state.auth.email;
export const selectUser = (state) => state.auth.user;
export const selectUserRole = (state) => state.auth.user?.role;
export const selectUserPermissions = createSelector(
  [selectUser],
  (user) => user?.permissions || []
);
export const selectAuthStatus = (state) => state.auth.status;
export const selectAuthError = (state) => state.auth.error;
export const selectIsVerified = (state) => state.auth.isVerified;
export const selectAwaitingVerification = (state) => state.auth.awaitingVerification;
export const selectIsAuthenticated = (state) => !!state.auth.token && state.auth.isVerified;
export const selectIsAuthReady = (state) => state.auth.isAuthReady;
export const selectTimerValue = (state) => state.auth.timerValue;
export const selectAccessDenied = (state) => state.auth.accessDenied;

// **NEW: Hybrid Authentication Selectors**
export const selectAccessDeniedReason = (state) => state.auth.accessDeniedReason;
export const selectAuthMethod = (state) => state.auth.authMethod;
export const selectValidationResult = (state) => state.auth.validationResult;
export const selectTenantModules = createSelector(
  [selectUser],
  (user) => user?.tenantModules || []
);

// **RBAC: Memoized selector for permission sources to prevent unnecessary re-renders**
const selectValidationResultRaw = (state) => state.auth.validationResult;

export const selectPermissionSources = createSelector(
  [selectValidationResultRaw],
  (validationResult) => {
    const sources = validationResult?.sources;
    if (!sources) {
      // Return a stable empty object to prevent unnecessary re-renders
      return { jwt: [], api: [], tenant: [] };
    }
    return {
      jwt: sources.jwt || [],
      api: sources.api || [],
      tenant: sources.tenant || []
    };
  }
);

export const { resetAuthState, clearAuthError, updateTimerValue, clearUserCache, clearLoginSession, setAuthReady, syncLogoutFromOtherTab } = authSlice.actions;
export default authSlice.reducer;